import { useEffect, useState } from "react";
import { Download, X, Smartphone, Share2 } from "lucide-react";
import { track, EVENTS } from "@/lib/analytics";

/**
 * InstallPrompt — a non-blocking "Install AlKoncierge" banner.
 *
 * Behaviour:
 *   - Chrome/Edge/Samsung Internet expose a `beforeinstallprompt` event.
 *     We capture it and surface a custom prompt — far higher install rates
 *     than relying on the browser's mini-infobar (which Apple doesn't show
 *     anyway).
 *   - iOS Safari doesn't fire that event, so we show a one-time tooltip
 *     explaining the Share → Add to Home Screen flow. Detected via UA.
 *   - User can dismiss; we persist that decision in localStorage so we
 *     don't nag on every page load.
 *   - Hidden entirely when the app is *already* installed (display-mode:
 *     standalone) — no point prompting to install what's already installed.
 */
const DISMISS_KEY = "alk_install_dismissed_at";
const DISMISS_TTL_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

function isStandalone() {
  try {
    return (
      window.matchMedia &&
      window.matchMedia("(display-mode: standalone)").matches
    ) || window.navigator.standalone === true;
  } catch {
    return false;
  }
}

function recentlyDismissed() {
  try {
    const ts = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    return Date.now() - ts < DISMISS_TTL_MS;
  } catch {
    return false;
  }
}

function isIos() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iphone|ipad|ipod/i.test(ua) && !/crios|fxios/i.test(ua);
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [showIos, setShowIos] = useState(false);

  useEffect(() => {
    if (isStandalone() || recentlyDismissed()) return undefined;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    // Listening for `appinstalled` lets us proactively hide the banner the
    // moment the OS installs us, even if our handler didn't get a clean
    // outcome (some browsers don't resolve the `userChoice` promise on iOS
    // Safari / certain Android Chrome builds).
    const onInstalled = () => {
      setDeferred(null);
      setShowIos(false);
      try {
        localStorage.setItem(DISMISS_KEY, String(Date.now()));
      } catch {
        /* ignore */
      }
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);

    // iOS path — give Safari users a one-time hint after a short delay.
    let iosTimer;
    if (isIos()) {
      iosTimer = setTimeout(() => setShowIos(true), 4000);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferred) return;
    // Hide our custom banner immediately — the native OS prompt is taking
    // over the screen, and on a couple of Android Chrome builds the
    // `userChoice` promise never resolves, leaving our banner stuck.
    const evt = deferred;
    setDeferred(null);
    track(EVENTS.INSTALL_PROMPT_ACCEPTED);
    try {
      evt.prompt();
      const choice = await evt.userChoice;
      if (choice?.outcome === "dismissed") {
        try {
          localStorage.setItem(DISMISS_KEY, String(Date.now()));
        } catch {
          /* ignore quota / private mode */
        }
      }
    } catch (err) {
      console.warn("[install] prompt failed", err);
    }
  };

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* ignore quota / private mode */
    }
    setDeferred(null);
    setShowIos(false);
  };

  if (deferred) {
    return (
      <div
        data-testid="install-prompt"
        className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-sm z-[60] rounded-2xl bg-[#2A3B2C] text-white shadow-2xl border border-white/10 p-4 flex items-start gap-3"
      >
        <div className="w-10 h-10 rounded-xl bg-[#D95D39] flex items-center justify-center shrink-0">
          <Smartphone className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-sm font-semibold">
            Install AlKoncierge
          </div>
          <p className="text-xs text-white/70 mt-0.5 leading-relaxed">
            Add AlKoncierge to your home screen for one-tap access to halal
            spots — works offline too.
          </p>
          <div className="mt-3 flex gap-2">
            <button
              data-testid="install-prompt-accept"
              onClick={handleInstall}
              className="h-9 px-4 rounded-full bg-[#D95D39] hover:bg-[#C54E2C] text-white text-xs font-medium inline-flex items-center gap-1.5"
            >
              <Download className="w-3.5 h-3.5" />
              Install
            </button>
            <button
              data-testid="install-prompt-dismiss"
              onClick={dismiss}
              className="h-9 px-3 rounded-full bg-white/10 hover:bg-white/20 text-white/80 text-xs font-medium"
            >
              Not now
            </button>
          </div>
        </div>
        <button
          onClick={dismiss}
          className="text-white/60 hover:text-white shrink-0"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  if (showIos) {
    return (
      <div
        data-testid="install-ios-tip"
        className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-sm z-[60] rounded-2xl bg-white shadow-2xl border border-[#E5E1D8] p-4 flex items-start gap-3"
      >
        <div className="w-10 h-10 rounded-xl bg-[#2A3B2C] flex items-center justify-center shrink-0">
          <Share2 className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-sm font-semibold text-[#2A3B2C]">
            Add AlKoncierge to your Home Screen
          </div>
          <p className="text-xs text-[#647466] mt-0.5 leading-relaxed">
            Tap the <span className="font-semibold">Share</span> icon in
            Safari, then <span className="font-semibold">Add to Home Screen</span>{" "}
            to launch AlKoncierge like a native app.
          </p>
        </div>
        <button
          onClick={dismiss}
          className="text-[#8A9A8B] hover:text-[#2A3B2C] shrink-0"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return null;
}
